import { getInstrumentUnifiedKind } from '@/features/order/order-form/lib/instrument-filter'
import {
  resolveBaseCurrency,
  resolveQuoteCurrency,
} from '@/features/order/order-form/lib/instrument-resolution'
import { resolveOptionInstrument } from '@/features/order/order-form/lib/parse-option-instrument'
import type { OrderFormSelection } from '@/state/order'
import type { SelectedInstrument, SelectedOption } from '@/types/dashboard'
import { type Instrument, InstrumentKind } from '@/types/generated/common/types'

/**
 * Order Form rows built straight from catalog metadata: the on-widget instrument picker starts
 * from an `Instrument`, never from a parsed symbol. The row is
 * `inherited`, and its accountId is left for the form's own account resolution to fill.
 */
export function buildSelectedInstrumentFromCatalog(
  instrument: Instrument,
  side: 'buy' | 'sell' = 'buy'
): SelectedInstrument {
  return {
    side,
    accountBinding: { kind: 'inherited' },
    symbol: instrument.symbol,
    baseCurrency: resolveBaseCurrency([], instrument.symbol, instrument),
    quoteCurrency: resolveQuoteCurrency([], instrument.symbol, instrument),
    instrumentKind: getInstrumentUnifiedKind(instrument),
    exchange: instrument.exchange.toString(),
    exchangeSymbol: instrument.exchangeSymbol,
    exchangeId: instrument.exchangeId,
    internalId: instrument.internalId,
    settlementCurrency: instrument.settlementCurrency,
    tickSize: instrument.tickSize,
    minTradeAmount: instrument.minTradeAmount,
    contractSize: instrument.contractSize,
    pricePrecision: instrument.pricePrecision,
    qtyPrecision: instrument.qtyPrecision,
    expirationTsMs: instrument.expirationTsMs,
    fundingInterval: instrument.fundingInterval,
    type: instrument.type,
  }
}

export function buildSelectedOptionFromCatalog(
  instrument: Instrument,
  tradeSide: 'buy' | 'sell' = 'buy'
): SelectedOption {
  const parsed = resolveOptionInstrument(instrument.symbol, instrument)
  return {
    instrumentName: instrument.symbol,
    accountBinding: { kind: 'inherited' },
    side: tradeSide === 'buy' ? 'ask' : 'bid',
    tradeSide,
    price: '-',
    exchange: instrument.exchange.toString(),
    minTradeAmount: instrument.minTradeAmount,
    strikePrice: parsed ? String(parsed.strike) : undefined,
    optionType: parsed ? (parsed.type === 'C' ? 'call' : 'put') : undefined,
  }
}

export function isOptionCatalogInstrument(instrument: Instrument): boolean {
  return instrument.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
}

/** A selection holding exactly `instrument`, as a buy. Used when the form is empty. */
export function selectionForInstrument(instrument: Instrument): OrderFormSelection {
  return isOptionCatalogInstrument(instrument)
    ? { selectedInstruments: [], selectedOptions: [buildSelectedOptionFromCatalog(instrument)] }
    : { selectedInstruments: [buildSelectedInstrumentFromCatalog(instrument)], selectedOptions: [] }
}
